/**
 * ModerationActionMenu Component
 *
 * Action menu for moderation case cards providing quick actions.
 * Follows the same pattern as the audit ActionMenu component.
 *
 * @see {@link apps/soteria-ui/src/internal-audit/components/AuditDecisionPanel/components/ActionMenu.tsx}
 */

import { Menu, MenuItem } from '@soteria/design-system-ve';
import { useCallback } from 'react';

export interface ModerationActionMenuProps {
  /** Whether the action menu is open */
  isOpen: boolean;
  /** ID of the trigger button for accessibility */
  triggerId: string;
  /** Callback when menu should close */
  onClose: () => void;
  /** Callback when Classification action is selected */
  onClassification: () => void;
  /** Callback when Context action is selected (feature-flagged — omit to hide) */
  onContext?: () => void;
}

/**
 * ModerationActionMenu component for moderation case card actions
 * Provides classification functionality via a dropdown menu
 */
export function ModerationActionMenu({ isOpen, triggerId, onClose, onClassification, onContext }: ModerationActionMenuProps) {
  const handleClassificationClick = useCallback(() => {
    onClose();
    onClassification();
  }, [onClose, onClassification]);

  const handleContextClick = useCallback(() => {
    onClose();
    onContext?.();
  }, [onClose, onContext]);

  return (
    <Menu
      ariaLabel="Moderation case actions menu"
      isOpen={isOpen}
      placement="bottom end"
      showCloseButton={false}
      triggerId={triggerId}
      onOpenChange={menuIsOpen => {
        if (!menuIsOpen) {
          onClose();
        }
      }}
    >
      <MenuItem label="Classification" onClick={handleClassificationClick} />
      {onContext && <MenuItem label="Context" onClick={handleContextClick} />}
    </Menu>
  );
}
